<template>
	<div class="PanelDealAddresses">
		<div
			v-for="(addressQuestion, index) in dealAddressesQuestions"
			:key="index"
		>
			<div
				class="radio-button-inline"
				:invalid="util.isNull(validation) ? false : validation.dealAddressesQuestions[index].$invalid"
				:style="$grid('411px-1')"
			>
				<span>{{ addressQuestion.question }}</span>

				<div class="no-padding-grid">
					<InputRadio
						label="Yes"
						:valueMatch="true"
						:modelValue="addressQuestion.answer"
						@update:modelValue="val => onAnswerAddressQuestion(val, index, addressQuestion)"
						:isButton="true"
					/>

					<InputRadio
						label="No"
						:valueMatch="false"
						:modelValue="addressQuestion.answer"
						@update:modelValue="val => onAnswerAddressQuestion(val, index, addressQuestion)"
						:isButton="true"
					/>
				</div>
			</div>

			<Panel
				v-if="addressQuestion.answer && indexInDealAddresses(addressQuestion.type) !== -1"
				class="deal-address-panel"
				:title="addressQuestion.title"
				:action="getAvailableCopyActions(addressQuestion.type)"
			>
				<AddressComponent
					:data="fimenu.dealAddresses[indexInDealAddresses(addressQuestion.type)]"
					:validation="validation.dealAddresses[indexInDealAddresses(addressQuestion.type)]"
					countryQuestionText="Is the address in the US?"
					searchable
					panelView
					showCounty
				/>
			</Panel>
		</div>
	</div>
</template>
<script lang="ts">
	export interface IPanelDealAddressesProps {
		fimenu: FIMenu;
		validation: any;
		disabled: boolean;
	}
</script>
<script setup lang="ts">
	import { computed, inject, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';
	import util from '@core/services/util';

	import ENUMS from '@core/classes/Enums';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuAddress from '@core/classes/FIMenuAddress';

	import AddressComponent from '@/components/AddressComponent.vue';
	import { DealRoomHubPlugin } from '@core/plugins/dealRoomHub';
	import { Emitter } from 'mitt';
	import InputRadio from '@core/components/InputRadio.vue';
	import Panel from '@core/components/Panel.vue';

	const { disabled, fimenu, validation } = defineProps<IPanelDealAddressesProps>();
	const EventBus = inject<Emitter<any>>('EventBus', null);
	const $dealRoomHub = inject<DealRoomHubPlugin>('$dealRoomHub');

	const dealAddressesQuestions = ref(fimenu.dealAddressesQuestions.map((i: any) => i));
	const addressActions = ref([]);
	const dealUpdatedExternally = ref(false);

	const dealAddresses = computed((): FIMenuAddress[] => fimenu.dealAddresses);

	onBeforeMount(() => {
		EventBus.on('dealUpdatedExternally', onDealUpdatedExternally);
	});

	onBeforeUnmount(() => {
		EventBus.off('dealUpdatedExternally', onDealUpdatedExternally);
	});

	watch(
		() => fimenu.dealAddressesQuestions,
		function (newVal) {
			util.debounce(onDealAddressQuestionsUpdated, 100, newVal);
		},
		{ deep: true },
	);

	function onDealUpdatedExternally(newVal: boolean) {
		dealUpdatedExternally.value = newVal;
	}

	function onDealAddressQuestionsUpdated(newVal: any) {
		if (!$dealRoomHub.connected) return;

		// Keep radio buttons synced with FI Menu updates
		for (let i = 0; i < newVal.length; i++) {
			if (dealAddressesQuestions.value[i]) dealAddressesQuestions.value[i].answer = newVal[i].answer;
		}
	}

	function onAnswerAddressQuestion(isSelected: boolean, index: number, addressQuestion: any) {
		if (disabled || dealUpdatedExternally.value) return;

		if (isSelected) {
			const newAddress = new FIMenuAddress();
			newAddress.addressType = addressQuestion.type;
			fimenu.setAddress(addressQuestion.type, newAddress);
		} else {
			fimenu.removeAddress(addressQuestion.type);
		}

		fimenu.setDealAddressQuestionAnswer(dealAddressesQuestions.value[index].type, isSelected);
	}

	function indexInDealAddresses(type: number): number {
		return fimenu.indexInDealAddresses(type);
	}

	function getAvailableCopyActions(currentType: number): { text: string; onClick: () => void }[] {
		const availableActions = [];

		for (let i = 0; i < dealAddresses.value.length; i++) {
			if (dealAddresses.value[i].addressType === currentType) continue;

			const addressEnumObj = ENUMS.addressType.find(a => a.type === dealAddresses.value[i].addressType);
			if (!addressEnumObj) continue;

			availableActions.push({
				text: `Copy address from ${addressEnumObj.description}`,
				onClick: () => fimenu.copyDealAddress(currentType, dealAddresses.value[i]),
			});
		}

		return availableActions;
	}
</script>
<style>
	.deal-address-panel {
		margin-bottom: 10px;
	}
</style>
